import { Route } from "react-router";
import type { ReactElement } from "react";
import { lazy } from "react";
import { AdminRoute } from "@/app/AdminRoute";
import { PartnerRoute } from "@/app/PartnerRoute";
import { ProtectedRoute } from "@/app/ProtectedRoute";
import {
  loadAnalysisPage,
  loadCardsSelectionPage,
} from "@/app/prefetchSpreadFlowPages";

const Dashboard = lazy(() => import("@/pages/dashboard").then((m) => ({ default: m.SpreadsSelection })));
const CardsSelection = lazy(loadCardsSelectionPage);
const AnalysisPage = lazy(loadAnalysisPage);
const CardOfTheDay = lazy(() => import("@/pages/card-of-the-day").then((m) => ({ default: m.CardOfTheDay })));
const CODMeaningPage = lazy(() => import("@/pages/cod-meaning").then((m) => ({ default: m.CODMeaningPage })));
const PricingPage = lazy(() => import("@/pages/pricing").then((m) => ({ default: m.PricingPage })));
const PaymentCompletePage = lazy(() =>
  import("@/pages/payment-complete").then((m) => ({ default: m.PaymentCompletePage }))
);
const ReadingDraftPage = lazy(() =>
  import("@/pages/reading-draft").then((m) => ({ default: m.ReadingDraftPage }))
);
const UserProfilePage = lazy(() => import("@/pages/user-profile"));
const BalancePage = lazy(() => import("@/pages/balance"));
const NewsLayout = lazy(() => import("@/pages/news/layout"));
const NewsListPage = lazy(() => import("@/pages/news"));
const NewsDetailPage = lazy(() => import("@/pages/news/detail"));
const AdminPartnersPage = lazy(() => import("@/pages/admin/partners"));
const AdminPartnerPage = lazy(() => import("@/pages/admin/partner"));
const PartnerPage = lazy(() => import("@/pages/partner"));

/**
 * Shared route elements — must be `<Route>` nodes (not a wrapper component), because
 * React Router only accepts `Route` or `Fragment` as direct children of `<Routes>`.
 */
export const mainLayoutRouteElements: ReactElement[] = [
  <Route
    key="ask"
    path="/ask"
    element={
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    }
  />,
  <Route
    key="daily"
    path="/daily"
    element={
      <ProtectedRoute>
        <CardOfTheDay />
      </ProtectedRoute>
    }
  />,
  <Route
    key="daily-meaning"
    path="/daily/meaning"
    element={
      <ProtectedRoute>
        <CODMeaningPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="draw"
    path="/draw"
    element={
      <ProtectedRoute>
        <CardsSelection />
      </ProtectedRoute>
    }
  />,
  <Route
    key="draft"
    path="/draft"
    element={
      <ProtectedRoute>
        <ReadingDraftPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="reading"
    path="/reading"
    element={
      <ProtectedRoute>
        <AnalysisPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="pricing"
    path="/pricing"
    element={
      <ProtectedRoute>
        <PricingPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="payment-complete"
    path="/payment/complete"
    element={
      <ProtectedRoute>
        <PaymentCompletePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="profile"
    path="/profile"
    element={
      <ProtectedRoute>
        <UserProfilePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="balance"
    path="/balance"
    element={
      <ProtectedRoute>
        <BalancePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="partner"
    path="/partner"
    element={
      <ProtectedRoute>
        <PartnerRoute>
          <PartnerPage />
        </PartnerRoute>
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-partners"
    path="/admin/partners"
    element={
      <ProtectedRoute>
        <AdminRoute>
          <AdminPartnersPage />
        </AdminRoute>
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-partner"
    path="/admin/partners/:partnerId"
    element={
      <ProtectedRoute>
        <AdminRoute>
          <AdminPartnerPage />
        </AdminRoute>
      </ProtectedRoute>
    }
  />,
  <Route
    key="news"
    path="/news"
    element={
      <ProtectedRoute>
        <NewsLayout />
      </ProtectedRoute>
    }
  >
    <Route index element={<NewsListPage />} />
    <Route path=":id" element={<NewsDetailPage />} />
  </Route>,
];

export {
  prefetchDrawPage,
  prefetchReadingPage,
} from "@/app/prefetchSpreadFlowPages";
